import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { DocBlock } from "@/components/admin/DocBlocks";
import { getArticle } from "@/content/articles";
import { SITE } from "@/content/copy";
import { trackEvent } from "@/lib/analytics";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticle(slug);

  // BlogPosting structured data, generated from the same content that renders.
  useEffect(() => {
    if (!article) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "article-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.description,
      datePublished: article.date,
      author: {
        "@type": "Person",
        name: "Nima Riazati",
        jobTitle: "CPA, Founder",
        worksFor: { "@type": "Organization", name: "Clarus Partners" },
      },
      publisher: { "@type": "Organization", name: "Clarus Partners" },
      mainEntityOfPage: `${SITE.domain}/articles/${article.slug}`,
    });
    document.head.appendChild(script);
    return () => document.getElementById("article-jsonld")?.remove();
  }, [article]);

  if (!article) {
    return (
      <>
        <Seo title="Article not found | Clarus Partners" noindex />
        <Header />
        <main className="py-24 text-center">
          <h1 className="text-3xl font-extrabold text-navy">Article not found</h1>
          <Link to="/articles" data-testid="article-not-found-back" className="mt-4 inline-block text-terracotta font-bold">
            ← All articles
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Seo title={`${article.title} | Clarus Partners`} description={article.description} />
      <Header />
      <main className="py-16 lg:py-24">
        <article data-testid="article-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/articles" data-testid="article-back-link" className="text-sm font-bold text-navy/50 hover:text-navy transition-colors">
            ← All articles
          </Link>
          <p className="mt-8 text-xs font-bold uppercase tracking-widest text-terracotta">
            {article.audience} · {article.readTime}
          </p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">{article.title}</h1>
          <p className="mt-3 text-sm text-navy/50">By Nima Riazati, CPA - Clarus Partners</p>

          <div className="mt-10">
            {article.blocks.map((block, i) => (
              <DocBlock key={i} block={block.type === "h3" ? { ...block, type: "h3" } : block} />
            ))}
          </div>

          <div className="mt-14 rounded-2xl bg-navy text-white p-8">
            <h2 className="text-xl font-bold">How healthy is your Xero file?</h2>
            <p className="mt-2 text-sm text-white/70 leading-relaxed">
              Run the free 15-point Xero Health Check and find out in about 20 minutes - or book a free
              30-minute call and talk it through.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link
                to="/xero-health-check"
                data-testid="article-checklist-cta"
                className="inline-flex justify-center items-center rounded-full bg-white px-6 py-3 text-sm font-bold text-navy transition-colors hover:bg-terracotta-light"
              >
                Get the free checklist
              </Link>
              <a
                href={SITE.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="article-book-cta"
                onClick={() => trackEvent("book_consultation_click", { location: `article:${article.slug}` })}
                className="inline-flex justify-center items-center rounded-full bg-terracotta px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-terracotta-hover"
              >
                {SITE.consultLabel}
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
